// src/data/memberStanding.js
//
// Bob, 2026-10-25: "a members standing should not be made public unless there is a good legal and ethical
// reason - working but not paying dues, working to harm the union or another member. It's internal business,
// get your nose out of it, he just needs to be able to indicate it to the lc and gc, mark the member somehow,
// like they use e-49."
//
// So this is NOT a dues ledger and NOT a derived "good standing" status. It is a PRIVATE MARK the local's
// Secretary & Treasurer places on a member to INDICATE a standing concern to the Local Chairperson and the
// General Chairperson. It indicates; it does not account for money. It is never public and never shown to the
// member - internal business. A mark is placed, and later lifted; both are attributed and dated (append-only).
//
// Records: memberStanding/{local}/marks/{markId} - the callable writes; firestore.rules says the local's S&T
// writes, and only the member's LC, the committee's GC, and the administrator read. No member read, nothing public.
//
// PURE. No Firestore, no clock (today is passed in). Shared with the server (scripts\sync-shared-modules.js).

// The only legitimate reasons, from Bob's own words. 'other' carries a stated note so a mark always says why.
export const MARK_REASONS = Object.freeze({
  dues_delinquent_while_working: 'Working but not paying dues',
  harm_to_union_or_member: 'Working to harm the union or another member',
  other: 'Another standing concern, stated in the note',
});
export const MARK_REASON_KEYS = Object.freeze(Object.keys(MARK_REASONS));

// Which track a reason runs on, from the constitution (refined 2026-10-26):
//   good_standing = dues (Sec. 9(b)) - MECHANICAL, so it can be AUTO-derived/flagged and accounted at the meeting.
//   conduct       = Art. 3 Sec. 3 discipline, which needs CHARGES AND TRIAL - so it is a REFERRAL to the body,
//                   never an automatic or unilateral finding. 'other' defaults to conduct (the body decides).
export const REASON_TRACK = Object.freeze({
  dues_delinquent_while_working: 'good_standing',
  harm_to_union_or_member: 'conduct',
  other: 'conduct',
});
export function trackOf(reason) { return REASON_TRACK[reason] || ''; }
export function isAutomatable(reason) { return REASON_TRACK[reason] === 'good_standing'; }

// The local meeting reviews standing in session (Bob 2026-10-26); the committee-in-whole decides. This records
// that review and the body's decision - the platform records, it does not decide. 'referred_charges' is the only
// path to discipline, and it is the Sec. 3 charges-and-trial process, not a result the platform can reach itself.
export const MEETING_DECISIONS = Object.freeze({
  accounted_good_standing: 'Reviewed - member in good standing',
  accounted_delinquent: 'Reviewed - dues delinquent, accounted',
  dues_brought_current: 'Dues brought current',
  referred_charges: 'Referred to charges and trial (Constitution Art. 3, Sec. 3)',
  no_action: 'Reviewed - no action',
  other: 'Other action, stated in the note',
});
export const MEETING_DECISION_KEYS = Object.freeze(Object.keys(MEETING_DECISIONS));

// Who may see a mark - stated here so a caller cannot read it as "public". Enforcement is firestore.rules.
export const VISIBLE_TO = Object.freeze(['the local Secretary & Treasurer who placed it', "the member's Local Chairperson",
  "the committee's General Chairperson", 'the administrator']);

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const s = (v) => (v == null ? '' : String(v)).trim();
const validDate = (d) => ISO.test(d) && !Number.isNaN(Date.parse(d + 'T00:00:00Z')) && new Date(d + 'T00:00:00Z').toISOString().slice(0, 10) === d;

// Place a mark. input: { memberId, local, reason, note, by, on }. Returns { ok, errors, record }.
export function planMark(input, today) {
  const i = input || {};
  const errors = [];
  if (!s(i.memberId)) errors.push('The member is required.');
  if (!s(i.local)) errors.push('The local is required.');
  if (!MARK_REASON_KEYS.includes(i.reason)) errors.push('The reason must be one of: ' + MARK_REASON_KEYS.join(', ') + '.');
  if (i.reason === 'other' && !s(i.note)) errors.push('State the concern in the note.');
  if (s(i.note).length > 600) errors.push('The note is at most 600 characters.');
  if (!s(i.by)) errors.push('Who placed the mark is required.');
  if (!validDate(s(i.on))) errors.push('The date placed (YYYY-MM-DD) is required.');
  else if (today && i.on > today) errors.push('A date in the future has not happened yet.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], record: {
    memberId: s(i.memberId), local: s(i.local), reason: i.reason, note: s(i.note) || null,
    state: 'marked', by: s(i.by), on: s(i.on), liftedBy: null, liftedOn: null, liftReason: null } };
}

// Lift a mark that is in place. input: { by, on, reason }. existing: the current mark record.
export function planLift(input, existing, today) {
  const i = input || {};
  const e = existing || {};
  const errors = [];
  if (e.state !== 'marked') errors.push('There is no mark in place to lift.');
  if (!s(i.by)) errors.push('Who lifted the mark is required.');
  if (!validDate(s(i.on))) errors.push('The date lifted (YYYY-MM-DD) is required.');
  else if (today && i.on > today) errors.push('A date in the future has not happened yet.');
  else if (e.on && i.on < e.on) errors.push('A mark cannot be lifted before it was placed.');
  if (s(i.reason).length > 600) errors.push('The lift reason is at most 600 characters.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], patch: { state: 'lifted', liftedBy: s(i.by), liftedOn: s(i.on), liftReason: s(i.reason) || null } };
}

// The member's current mark, if any, read off the marks on record (latest placed that is still in place).
export function currentMark(marks) {
  const list = (Array.isArray(marks) ? marks : []).filter((m) => m && m.state === 'marked')
    .sort((a, b) => (a.on < b.on ? 1 : a.on > b.on ? -1 : 0));
  return list[0] || null;
}

// Record that the local meeting reviewed a member's standing in session and what the body decided.
// input: { memberId, local, meetingDate, decision, note, by }. The decision is the body's; the platform records it.
export function planMeetingReview(input, today) {
  const i = input || {};
  const errors = [];
  if (!s(i.memberId)) errors.push('The member is required.');
  if (!s(i.local)) errors.push('The local is required.');
  if (!MEETING_DECISION_KEYS.includes(i.decision)) errors.push('The decision must be one of: ' + MEETING_DECISION_KEYS.join(', ') + '.');
  if (i.decision === 'other' && !s(i.note)) errors.push('State the action in the note.');
  if (s(i.note).length > 600) errors.push('The note is at most 600 characters.');
  if (!s(i.by)) errors.push('Who recorded the review is required.');
  if (!validDate(s(i.meetingDate))) errors.push('The meeting date (YYYY-MM-DD) is required.');
  else if (today && i.meetingDate > today) errors.push('A meeting in the future has not happened yet.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], record: {
    kind: 'meeting_review', memberId: s(i.memberId), local: s(i.local), meetingDate: s(i.meetingDate),
    decision: i.decision, note: s(i.note) || null, by: s(i.by), on: s(i.meetingDate) } };
}

// A short label for a mark, for the LC/GC view. Never includes the note unless asked (it may name another member).
export function markLabel(mark) {
  if (!mark || mark.state !== 'marked') return '';
  return 'Standing mark: ' + (MARK_REASONS[mark.reason] || mark.reason) + ' (placed ' + mark.on + ')';
}
